import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import { db, storage } from '../../../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// ── Types ──────────────────────────────────────────────────────────────────

type Step = 'upload' | 'map' | 'preview' | 'importing' | 'complete' | 'documents';

interface CSVRow {
  [key: string]: string;
}

interface ColumnMapping {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  status: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  insuranceProvider: string;
  policyNumber: string;
  groupNumber: string;
  presentingConcern: string;
}

interface ImportResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: { row: number; name: string; error: string }[];
  createdPatients: { id: string; name: string }[];
}

const CHART_ORDER_CATEGORIES = [
  { id: 'legals', label: 'Legals' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'treatment-plans', label: 'Treatment Plans' },
  { id: 'progress-notes', label: 'Progress Notes' },
  { id: 'correspondence', label: 'Correspondence' },
  { id: 'insurance-billing', label: 'Insurance / Billing' },
  { id: 'miscellaneous', label: 'Miscellaneous' },
  { id: 'discharge', label: 'Discharge' },
] as const;

const PATIENT_FIELDS: { key: keyof ColumnMapping; label: string; required: boolean }[] = [
  { key: 'firstName', label: 'First Name', required: true },
  { key: 'lastName', label: 'Last Name', required: true },
  { key: 'dateOfBirth', label: 'Date of Birth', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'status', label: 'Status (active/inactive)', required: false },
  { key: 'address', label: 'Address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zipCode', label: 'Zip Code', required: false },
  { key: 'insuranceProvider', label: 'Insurance Provider', required: false },
  { key: 'policyNumber', label: 'Policy Number', required: false },
  { key: 'groupNumber', label: 'Group Number', required: false },
  { key: 'presentingConcern', label: 'Presenting Concern', required: false },
];

// ── CSV Parser ─────────────────────────────────────────────────────────────

function parseCSV(text: string): { headers: string[]; rows: CSVRow[] } {
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows: CSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    const row: CSVRow = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || '';
    });
    rows.push(row);
  }
  return { headers, rows };
}

// ── Auto-map heuristic ─────────────────────────────────────────────────────

function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    firstName: '', lastName: '', dateOfBirth: '', email: '', phone: '',
    status: '', address: '', city: '', state: '', zipCode: '',
    insuranceProvider: '', policyNumber: '', groupNumber: '', presentingConcern: '',
  };

  const lower = headers.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));

  const match = (patterns: string[]): string => {
    for (const p of patterns) {
      const idx = lower.findIndex(h => h.includes(p));
      if (idx !== -1) return headers[idx];
    }
    return '';
  };

  mapping.firstName = match(['firstname', 'first']);
  mapping.lastName = match(['lastname', 'last', 'surname']);
  mapping.dateOfBirth = match(['dateofbirth', 'dob', 'birthdate', 'birthday']);
  mapping.email = match(['email', 'emailaddress']);
  mapping.phone = match(['phone', 'telephone', 'mobile', 'cell']);
  mapping.status = match(['status']);
  mapping.address = match(['address', 'street', 'addr']);
  mapping.city = match(['city']);
  mapping.state = match(['state', 'province']);
  mapping.zipCode = match(['zip', 'zipcode', 'postal']);
  mapping.insuranceProvider = match(['insurance', 'insurer', 'carrier']);
  mapping.policyNumber = match(['policy', 'policynumber', 'memberid']);
  mapping.groupNumber = match(['group', 'groupnumber', 'groupid']);
  mapping.presentingConcern = match(['concern', 'presenting', 'reason', 'complaint', 'chief']);

  return mapping;
}

// ── Component ──────────────────────────────────────────────────────────────

const DataImport: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState<Step>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<CSVRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<ColumnMapping>(autoMapColumns([]));
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importProgress, setImportProgress] = useState(0);

  // Document upload state
  const [docPatientId, setDocPatientId] = useState('');
  const [docCategory, setDocCategory] = useState('legals');
  const [docDate, setDocDate] = useState('');
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docUploadResults, setDocUploadResults] = useState<{ name: string; ok: boolean }[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  // ── Step 1: CSV Upload ─────────────────────────────────────────────────

  const handleCSVUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const { headers, rows } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        toast.error('Import Error', 'Could not parse CSV file. Please check the format.');
        return;
      }
      setCsvHeaders(headers);
      setCsvRows(rows);
      setMapping(autoMapColumns(headers));
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  // ── Step 2: Column Mapping ─────────────────────────────────────────────

  const updateMapping = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({ ...prev, [field]: value }));
  };

  const canProceedToPreview = mapping.firstName && mapping.lastName && mapping.dateOfBirth;

  // ── Step 3: Preview ────────────────────────────────────────────────────

  const getMappedValue = (row: CSVRow, field: keyof ColumnMapping): string => {
    const col = mapping[field];
    return col ? (row[col] || '') : '';
  };

  // ── Step 4: Import ────────────────────────────────────────────────────

  const runImport = async () => {
    if (!user?.uid) return;
    setStep('importing');
    setImportProgress(0);

    const result: ImportResult = { total: csvRows.length, succeeded: 0, failed: 0, errors: [], createdPatients: [] };

    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i];
      const first = getMappedValue(row, 'firstName').trim();
      const last = getMappedValue(row, 'lastName').trim();

      if (!first || !last) {
        result.failed++;
        result.errors.push({ row: i + 2, name: `${first} ${last}`.trim() || '(empty)', error: 'Missing first or last name' });
        setImportProgress(Math.round(((i + 1) / csvRows.length) * 100));
        continue;
      }

      const dob = getMappedValue(row, 'dateOfBirth').trim();
      const statusVal = getMappedValue(row, 'status').trim().toLowerCase();
      const patientStatus: 'active' | 'inactive' = statusVal === 'inactive' ? 'inactive' : 'active';

      const patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'> = {
        firstName: first,
        lastName: last,
        dateOfBirth: dob || '',
        email: getMappedValue(row, 'email').trim(),
        phone: getMappedValue(row, 'phone').trim(),
        status: patientStatus,
        assignedProviderId: user.uid,
        address: getMappedValue(row, 'address').trim() || undefined,
        city: getMappedValue(row, 'city').trim() || undefined,
        state: getMappedValue(row, 'state').trim() || undefined,
        zipCode: getMappedValue(row, 'zipCode').trim() || undefined,
        presentingConcern: getMappedValue(row, 'presentingConcern').trim() || undefined,
        intakeDate: new Date().toISOString().split('T')[0],
      };

      const insProvider = getMappedValue(row, 'insuranceProvider').trim();
      const policyNum = getMappedValue(row, 'policyNumber').trim();
      if (insProvider || policyNum) {
        patientData.insurance = {
          provider: insProvider,
          policyNumber: policyNum,
          groupNumber: getMappedValue(row, 'groupNumber').trim() || undefined,
        };
      }

      try {
        const id = await FirebasePatientService.createPatient(patientData);
        result.succeeded++;
        result.createdPatients.push({ id, name: `${first} ${last}` });
      } catch (err: any) {
        result.failed++;
        result.errors.push({ row: i + 2, name: `${first} ${last}`, error: err.message || 'Unknown error' });
      }

      setImportProgress(Math.round(((i + 1) / csvRows.length) * 100));
    }

    setImportResult(result);
    setStep('complete');
  };

  // ── Document Upload ──────────────────────────────────────────────────

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user?.uid || !docPatientId) return;

    setUploadingDocs(true);
    const results: { name: string; ok: boolean }[] = [];

    for (const file of Array.from(files)) {
      try {
        const storagePath = `client_files/${user.uid}/${docPatientId}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        await addDoc(collection(db, 'client_files'), {
          clientId: docPatientId,
          providerId: user.uid,
          fileName: file.name,
          category: docCategory,
          documentDate: docDate || '',
          downloadUrl,
          storagePath,
          fileSize: file.size,
          contentType: file.type,
          uploadedBy: user.displayName || user.email || user.uid,
          uploadedAt: Timestamp.now(),
        });

        results.push({ name: file.name, ok: true });
      } catch (err: any) {
        results.push({ name: file.name, ok: false });
      }
    }

    setDocUploadResults(prev => [...results, ...prev]);
    setUploadingDocs(false);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  // ── CSV Template Download ─────────────────────────────────────────────

  const downloadTemplate = () => {
    const headers = [
      'First Name', 'Last Name', 'Date of Birth', 'Email', 'Phone',
      'Status', 'Address', 'City', 'State', 'Zip Code',
      'Insurance Provider', 'Policy Number', 'Group Number', 'Presenting Concern'
    ];
    const sample = [
      'Jane', 'Doe', '1990-05-15', 'jane@example.com', '555-123-4567',
      'active', '123 Main St', 'Atlanta', 'GA', '30301',
      'Blue Cross', 'BC123456', 'GRP001', 'Anxiety and stress management'
    ];
    const csv = headers.join(',') + '\n' + sample.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Reset ─────────────────────────────────────────────────────────────

  const resetImport = () => {
    setStep('upload');
    setCsvHeaders([]);
    setCsvRows([]);
    setFileName('');
    setMapping(autoMapColumns([]));
    setImportResult(null);
    setImportProgress(0);
    setDocUploadResults([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '32px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
          Data Import
        </h1>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
          Import patients from CSV and upload scanned documents to their charts
        </p>
      </div>

      {/* Step Indicator */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', alignItems: 'center' }}>
        {[
          { id: 'upload', label: '1. Upload CSV' },
          { id: 'map', label: '2. Map Columns' },
          { id: 'preview', label: '3. Preview' },
          { id: 'importing', label: '4. Import' },
          { id: 'complete', label: '5. Done' },
        ].map((s, i) => {
          const isCurrent = step === s.id || (step === 'documents' && s.id === 'complete');
          const isPast = ['upload', 'map', 'preview', 'importing', 'complete'].indexOf(step) >
                         ['upload', 'map', 'preview', 'importing', 'complete'].indexOf(s.id);
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div style={{ width: '32px', height: '2px', backgroundColor: isPast ? '#28A745' : '#6CA6C1' }} />}
              <div style={{
                padding: '6px 14px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: isCurrent ? '#226F9E' : isPast ? '#28A745' : '#0A2540',
                color: '#FFFFFF',
                border: `1px solid ${isCurrent ? '#87CEEB' : isPast ? '#28A745' : '#6CA6C1'}`,
                whiteSpace: 'nowrap',
              }}>
                {isPast ? '✓ ' : ''}{s.label}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── STEP: Upload ─────────────────────────────────────────────── */}
      {step === 'upload' && (
        <>
          {/* Instructions */}
          <div style={{
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#F7E9A0', margin: '0 0 12px 0' }}>
              Getting Started
            </h3>
            <div style={{ color: '#FFFFFF', fontSize: '14px', lineHeight: '1.8' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>1.</strong> Download the CSV template or prepare your own CSV file with patient data.
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>2.</strong> Required columns: <span style={{ color: '#F7E9A0' }}>First Name, Last Name, Date of Birth</span>. All other fields are optional.
              </p>
              <p style={{ margin: '0 0 16px 0' }}>
                <strong>3.</strong> After importing patients, you can upload scanned documents (PDFs, images) to each patient's chart.
              </p>
              <button
                onClick={downloadTemplate}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#226F9E',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                Download CSV Template
              </button>
            </div>
          </div>

          {/* Upload Area */}
          <div
            style={{
              backgroundColor: '#113F67',
              border: '2px dashed #6CA6C1',
              borderRadius: '8px',
              padding: '48px',
              textAlign: 'center',
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file && file.name.endsWith('.csv')) {
                const fakeEvent = { target: { files: [file] } } as any;
                handleCSVUpload(fakeEvent);
              }
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0' }}>
              Upload your patient CSV file
            </h3>
            <p style={{ fontSize: '14px', color: '#87CEEB', margin: '0 0 24px 0' }}>
              Drag and drop or click to browse
            </p>
            <label style={{
              padding: '12px 32px',
              backgroundColor: '#226F9E',
              color: '#FFFFFF',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'inline-block',
            }}>
              Choose CSV file
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </>
      )}

      {/* ── STEP: Map Columns ────────────────────────────────────────── */}
      {step === 'map' && (
        <div style={{ backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 4px 0' }}>
                Map Your Columns
              </h3>
              <p style={{ fontSize: '13px', color: '#87CEEB', margin: 0 }}>
                File: {fileName} — {csvRows.length} rows detected. We auto-mapped what we could.
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
            {PATIENT_FIELDS.map(f => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{
                  width: '160px', fontSize: '13px', color: '#FFFFFF', flexShrink: 0,
                  fontWeight: f.required ? 700 : 400,
                }}>
                  {f.label}{f.required ? ' *' : ''}
                </label>
                <select
                  value={mapping[f.key]}
                  onChange={(e) => updateMapping(f.key, e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: '#0A2540',
                    color: '#FFFFFF',
                    border: `1px solid ${f.required && !mapping[f.key] ? '#ff6b6b' : '#6CA6C1'}`,
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}
                >
                  <option value="">— Skip / Not mapped —</option>
                  {csvHeaders.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setStep('upload')}
              style={{
                padding: '10px 24px', backgroundColor: 'transparent', color: '#87CEEB',
                border: '1px solid #6CA6C1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              Back
            </button>
            <button
              onClick={() => setStep('preview')}
              disabled={!canProceedToPreview}
              style={{
                padding: '10px 24px',
                backgroundColor: canProceedToPreview ? '#226F9E' : '#555',
                color: '#FFFFFF',
                border: 'none', borderRadius: '6px',
                cursor: canProceedToPreview ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: 600,
              }}
            >
              Preview Import ({csvRows.length} patients)
            </button>
          </div>
        </div>
      )}

      {/* ── STEP: Preview ────────────────────────────────────────────── */}
      {step === 'preview' && (
        <div>
          <div style={{
            backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
            padding: '24px', marginBottom: '24px',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0' }}>
              Preview — {csvRows.length} patients ready to import
            </h3>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 16px 0' }}>
              Showing first {Math.min(csvRows.length, 10)} rows. All patients will be assigned to you and marked as active.
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0A2540' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>First Name</th>
                    <th style={thStyle}>Last Name</th>
                    <th style={thStyle}>DOB</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Insurance</th>
                  </tr>
                </thead>
                <tbody>
                  {csvRows.slice(0, 10).map((row, i) => {
                    const first = getMappedValue(row, 'firstName');
                    const last = getMappedValue(row, 'lastName');
                    const missing = !first.trim() || !last.trim();
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #6CA6C1', backgroundColor: missing ? 'rgba(255,107,107,0.1)' : 'transparent' }}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={{ ...tdStyle, color: first.trim() ? '#FFFFFF' : '#ff6b6b' }}>{first || '(missing)'}</td>
                        <td style={{ ...tdStyle, color: last.trim() ? '#FFFFFF' : '#ff6b6b' }}>{last || '(missing)'}</td>
                        <td style={tdStyle}>{getMappedValue(row, 'dateOfBirth')}</td>
                        <td style={tdStyle}>{getMappedValue(row, 'email')}</td>
                        <td style={tdStyle}>{getMappedValue(row, 'phone')}</td>
                        <td style={tdStyle}>{getMappedValue(row, 'status') || 'active'}</td>
                        <td style={tdStyle}>{getMappedValue(row, 'insuranceProvider')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {csvRows.length > 10 && (
              <p style={{ fontSize: '12px', color: '#6CA6C1', marginTop: '8px', marginBottom: 0 }}>
                ...and {csvRows.length - 10} more rows
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setStep('map')}
              style={{
                padding: '10px 24px', backgroundColor: 'transparent', color: '#87CEEB',
                border: '1px solid #6CA6C1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              Back to Mapping
            </button>
            <button
              onClick={runImport}
              style={{
                padding: '10px 24px', backgroundColor: '#28A745', color: '#FFFFFF',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              }}
            >
              Import {csvRows.length} Patients
            </button>
          </div>
        </div>
      )}

      {/* ── STEP: Importing ──────────────────────────────────────────── */}
      {step === 'importing' && (
        <div style={{
          backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
          padding: '48px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 16px 0' }}>
            Importing patients...
          </h3>
          <div style={{
            width: '100%', maxWidth: '400px', margin: '0 auto 12px',
            backgroundColor: '#0A2540', borderRadius: '8px', height: '24px', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', backgroundColor: '#28A745', borderRadius: '8px',
              width: `${importProgress}%`, transition: 'width 0.3s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', color: '#FFFFFF', fontWeight: 600,
            }}>
              {importProgress}%
            </div>
          </div>
          <p style={{ color: '#87CEEB', fontSize: '14px', margin: 0 }}>
            Please wait — do not close this page
          </p>
        </div>
      )}

      {/* ── STEP: Complete ───────────────────────────────────────────── */}
      {step === 'complete' && importResult && (
        <div>
          {/* Summary Card */}
          <div style={{
            backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
            padding: '24px', marginBottom: '24px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(40,167,69,0.15)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px',
                border: '2px solid #28A745',
              }}>
                <span style={{ fontSize: '32px' }}>&#10003;</span>
              </div>
              <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#28A745', margin: '0' }}>
                All Done! Import Complete
              </h3>
              <p style={{ color: '#87CEEB', fontSize: '14px', margin: '4px 0 0 0' }}>
                Your patient data has been successfully imported into the system.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
              <div style={statBox}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF' }}>{importResult.total}</div>
                <div style={{ fontSize: '12px', color: '#87CEEB' }}>Total Rows</div>
              </div>
              <div style={statBox}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#28A745' }}>{importResult.succeeded}</div>
                <div style={{ fontSize: '12px', color: '#87CEEB' }}>Imported</div>
              </div>
              {importResult.failed > 0 && (
                <div style={statBox}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#ff6b6b' }}>{importResult.failed}</div>
                  <div style={{ fontSize: '12px', color: '#87CEEB' }}>Failed</div>
                </div>
              )}
            </div>

            {/* Errors */}
            {importResult.errors.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '14px', color: '#ff6b6b', margin: '0 0 8px 0' }}>Errors:</h4>
                {importResult.errors.map((err, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#FFFFFF', marginBottom: '4px' }}>
                    Row {err.row}: <strong>{err.name}</strong> — {err.error}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
            <button
              onClick={resetImport}
              style={{
                padding: '10px 24px', backgroundColor: '#226F9E', color: '#FFFFFF',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              }}
            >
              Import Another File
            </button>
            {importResult.createdPatients.length > 0 && (
              <button
                onClick={() => setStep('documents')}
                style={{
                  padding: '10px 24px', backgroundColor: '#F7E9A0', color: '#0A2540',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                }}
              >
                Upload Documents to Charts
              </button>
            )}
          </div>

          {/* Imported Patients List */}
          {importResult.createdPatients.length > 0 && (
            <div style={{
              backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
              padding: '24px',
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 12px 0' }}>
                Imported Patients
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                {importResult.createdPatients.map(p => (
                  <div key={p.id} style={{
                    padding: '8px 12px', backgroundColor: '#0A2540', borderRadius: '4px',
                    fontSize: '13px', color: '#FFFFFF',
                  }}>
                    {p.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP: Document Upload ────────────────────────────────────── */}
      {step === 'documents' && importResult && (
        <div>
          <div style={{
            backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
            padding: '24px', marginBottom: '24px',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 4px 0' }}>
              Upload Documents to Patient Charts
            </h3>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 20px 0' }}>
              Upload scanned PDFs and documents to the Digital Filing Cabinet for each imported patient.
            </p>

            {/* Patient Selector */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 250px' }}>
                <label style={{ fontSize: '12px', color: '#87CEEB', display: 'block', marginBottom: '4px' }}>Patient</label>
                <select
                  value={docPatientId}
                  onChange={(e) => setDocPatientId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', backgroundColor: '#0A2540', color: '#FFFFFF',
                    border: '1px solid #6CA6C1', borderRadius: '4px', fontSize: '14px',
                  }}
                >
                  <option value="">Select a patient...</option>
                  {importResult.createdPatients.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: '12px', color: '#87CEEB', display: 'block', marginBottom: '4px' }}>Chart Category</label>
                <select
                  value={docCategory}
                  onChange={(e) => setDocCategory(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', backgroundColor: '#0A2540', color: '#FFFFFF',
                    border: '1px solid #6CA6C1', borderRadius: '4px', fontSize: '14px',
                  }}
                >
                  {CHART_ORDER_CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '0 1 180px' }}>
                <label style={{ fontSize: '12px', color: '#87CEEB', display: 'block', marginBottom: '4px' }}>Document Date (optional)</label>
                <input
                  type="date"
                  value={docDate}
                  onChange={(e) => setDocDate(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', backgroundColor: '#0A2540', color: '#FFFFFF',
                    border: '1px solid #6CA6C1', borderRadius: '4px', fontSize: '14px',
                  }}
                />
              </div>
            </div>

            {/* Upload Button */}
            <label style={{
              padding: '10px 24px',
              backgroundColor: docPatientId ? '#226F9E' : '#555',
              color: '#FFFFFF',
              borderRadius: '6px',
              cursor: docPatientId ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: 600,
              display: 'inline-block',
              opacity: uploadingDocs ? 0.6 : 1,
            }}>
              {uploadingDocs ? 'Uploading...' : 'Choose Files to Upload'}
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.csv"
                multiple
                onChange={handleDocumentUpload}
                disabled={!docPatientId || uploadingDocs}
                style={{ display: 'none' }}
              />
            </label>
            <span style={{ fontSize: '12px', color: '#6CA6C1', marginLeft: '12px' }}>
              PDF, DOC, images, spreadsheets — multiple files OK
            </span>
          </div>

          {/* Upload Results */}
          {docUploadResults.length > 0 && (
            <div style={{
              backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px',
              padding: '24px', marginBottom: '24px',
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 12px 0' }}>
                Upload History
              </h4>
              {docUploadResults.map((r, i) => (
                <div key={i} style={{ fontSize: '13px', color: r.ok ? '#28A745' : '#ff6b6b', marginBottom: '4px' }}>
                  {r.ok ? '✓' : '✗'} {r.name}
                </div>
              ))}
            </div>
          )}

          <button
            onClick={resetImport}
            style={{
              padding: '10px 24px', backgroundColor: 'transparent', color: '#87CEEB',
              border: '1px solid #6CA6C1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
            }}
          >
            Done — Back to Import
          </button>
        </div>
      )}
    </div>
  );
};

// ── Shared Styles ──────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'left', color: '#F7E9A0',
  fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px', color: '#87CEEB', fontSize: '13px',
};

const statBox: React.CSSProperties = {
  backgroundColor: '#0A2540', borderRadius: '8px', padding: '16px 24px', textAlign: 'center',
};

export default DataImport;
